'use client'

import { MessageSquare, Send } from 'lucide-react'
import * as React from 'react'

import { Avatar, AvatarFallback, AvatarImage } from './avatar'
import {
  MentionEditor,
  type MentionEditorHandle,
  type MentionInputRef,
  type MentionItem,
  type MentionValue,
  mentionValueToInput,
  validMentionRanges,
} from './mention-editor'

/**
 * Shared task comment thread — the comment list + composer used by the
 * `AppTaskDetailPanel` right-side panel and by standalone task surfaces (e.g.
 * the apps/ordo school-onboarding task page). Headless on data: takes loaded
 * comments + the users referenced by them + an async `onComment` callback.
 * Keeping this in one place means the Customers detail panel and the school
 * task page render comments identically.
 */

export interface TaskCommentThreadComment {
  id: string
  authorId: string
  body: string
  createdAt: string
  edited?: boolean
  reactionsCount?: number
  /** Validated inline @mention ranges; rendered as highlighted spans. */
  mentions?: Array<{ offset: number; length: number; displayName: string }>
}

export interface TaskCommentThreadUser {
  id: string
  name: string
  email?: string
  image?: string | null
}

export interface TaskCommentThreadProps {
  comments: TaskCommentThreadComment[]
  users: TaskCommentThreadUser[]
  onComment: (body: string, mentions?: MentionInputRef[]) => Promise<void>
  /**
   * Async @mention suggestions. When provided, the composer upgrades from a
   * plain textarea to the MentionEditor and `onComment` receives the parsed
   * mention ranges as its second argument.
   */
  onMentionSearch?: (query: string) => MentionItem[] | Promise<MentionItem[]>
  /** Section heading. Defaults to "Comments". */
  heading?: string
  /** Hide the "MessageSquare" icon beside the heading. */
  hideHeading?: boolean
  /** Disable the composer (e.g. while the parent record is loading). */
  disabled?: boolean
  className?: string
  /** Tail of each comment (e.g. reactions toolbar). */
  renderCommentFooter?: (comment: TaskCommentThreadComment) => React.ReactNode
  /** Under the composer (mention suggestions, attach affordance). */
  composerFooter?: React.ReactNode
}

function initials(u: TaskCommentThreadUser | undefined): string {
  if (!u) {
    return '?'
  }
  const parts = (u.name ?? u.email ?? '').trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

function formatCommentTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return ''
  }
  const now = new Date()
  const sameYear = d.getFullYear() === now.getFullYear()
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

const EMPTY_MENTION_VALUE: MentionValue = { text: '', doc: null, mentions: [] }

export function TaskCommentThread({
  comments,
  users,
  onComment,
  onMentionSearch,
  heading = 'Comments',
  hideHeading = false,
  disabled = false,
  className,
  renderCommentFooter,
  composerFooter,
}: TaskCommentThreadProps) {
  const [commentText, setCommentText] = React.useState('')
  const [mentionValue, setMentionValue] = React.useState<MentionValue>(EMPTY_MENTION_VALUE)
  const [posting, setPosting] = React.useState(false)
  const mentionEditorRef = React.useRef<MentionEditorHandle>(null)
  const withMentions = Boolean(onMentionSearch)

  async function postComment() {
    const input = withMentions ? mentionValueToInput(mentionValue) : { body: commentText.trim(), mentions: [] }
    if (!input || !input.body || posting || disabled) {
      return
    }
    setPosting(true)
    try {
      await onComment(input.body, input.mentions)
      setCommentText('')
      setMentionValue(EMPTY_MENTION_VALUE)
      mentionEditorRef.current?.clear()
    } finally {
      setPosting(false)
    }
  }
  const canPost = withMentions ? Boolean(mentionValueToInput(mentionValue)?.body) : Boolean(commentText.trim())

  return (
    <div className={className}>
      {hideHeading ? null : (
        <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
          <MessageSquare className="h-4 w-4" /> {heading}
          {comments.length > 0 ? ` (${comments.length})` : ''}
        </h3>
      )}
      {comments.length > 0 ? (
        <div className="space-y-4 mb-3">
          {comments.map((c) => {
            const author = users.find((u) => u.id === c.authorId)
            return (
              <div key={c.id} className="group/comment relative flex gap-2">
                <Avatar className="h-7 w-7 shrink-0">
                  {author?.image ? <AvatarImage src={author.image} alt="" /> : null}
                  <AvatarFallback className="text-[10px]">{initials(author)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{author?.name ?? 'Someone'}</span>
                    <span className="text-xs text-muted-foreground">{formatCommentTime(c.createdAt)}</span>
                    {c.edited ? <span className="text-[11px] text-muted-foreground">· edited</span> : null}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{renderBodyWithMentions(c.body, c.mentions)}</p>
                  {renderCommentFooter?.(c)}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground mb-3">No comments yet.</p>
      )}

      {/* Composer — MentionEditor when the host wires suggestions, else textarea. */}
      <div className="flex items-end gap-2">
        {withMentions ? (
          <MentionEditor
            ref={mentionEditorRef}
            value={mentionValue}
            onChange={setMentionValue}
            onSearch={onMentionSearch}
            onSubmit={() => void postComment()}
            disabled={disabled}
            placeholder="Write a comment... (type @ to mention)"
          />
        ) : (
          <textarea
            value={commentText}
            disabled={disabled}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                void postComment()
              }
            }}
            placeholder="Write a comment..."
            rows={2}
            className="flex-1 text-sm bg-transparent border border-border rounded-md px-3 py-2 outline-none resize-none placeholder:text-muted-foreground/50 focus:border-primary/40 disabled:opacity-50"
          />
        )}
        <button
          type="button"
          onClick={() => void postComment()}
          disabled={!canPost || posting || disabled}
          aria-label="Send comment"
          className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 shrink-0 transition-colors">
          <Send className="h-4 w-4" />
        </button>
      </div>
      {composerFooter ? <div className="mt-2">{composerFooter}</div> : null}
    </div>
  )
}

/**
 * Splits a comment body into text + highlighted mention spans using the
 * validated offset ranges (never a regex over display names — duplicate or
 * substring names would mis-highlight).
 */
export function renderBodyWithMentions(
  body: string,
  mentions?: Array<{ offset: number; length: number; displayName: string }>,
): React.ReactNode {
  const valid = mentions && mentions.length > 0 ? validMentionRanges(body, mentions) : []
  if (valid.length === 0) {
    return body
  }
  const parts: React.ReactNode[] = []
  let cursor = 0
  for (const [index, mention] of valid.entries()) {
    if (mention.offset > cursor) {
      parts.push(body.slice(cursor, mention.offset))
    }
    parts.push(
      <span key={`mention-${index}`} className="text-primary font-medium">
        {body.slice(mention.offset, mention.offset + mention.length)}
      </span>,
    )
    cursor = mention.offset + mention.length
  }
  if (cursor < body.length) {
    parts.push(body.slice(cursor))
  }
  return parts
}
